import {
  useAccount,
  useGetOfferChangeDetailsQuery,
} from "@apis/accounts/useAccounts"
import type { IRetentionOffer } from "@apis/retention/types"
import type { IWinbackOffer } from "@apis/winbacks/types"
import { formatDate } from "@utils/formatters"

type IOffersDetailData = {
  long_script: string
  oldOffer: ISerializedOfferDetailData
  newOffer: ISerializedOfferDetailData
}

export type ISerializedOfferDetailData = Array<{
  title: string
  value: string | string[] | undefined
  alert?: string | boolean
}>

export const useOffersDetailSerializer = (
  account_id: string,
  offer: IWinbackOffer | IRetentionOffer,
) => {
  const getAccountQuery = useAccount(account_id)
  const getOfferChangeDetails = useGetOfferChangeDetailsQuery(account_id)

  const data: IOffersDetailData = {
    long_script: offer.long_script,
    oldOffer: [
      {
        title: "Old Offer",
        value: getAccountQuery.data?.offer_code,
      },
      {
        title: "Old Plan",
        value: getAccountQuery.data?.plan_name,
      },
      {
        title: "Current Rate",
        value:
          getOfferChangeDetails.data?.intro_rate_status?.intro_rate_message,
      },
      {
        title: "Historical Rewards Status",
        value: [
          ...(getOfferChangeDetails.data?.earned_rewards ?? []).map(
            (reward) =>
              `${reward.reward_description} vests on ${formatDate(reward.vest_dt)}`,
          ),
          ...(getOfferChangeDetails.data?.forfeitable_rewards ?? []).map(
            (reward) =>
              `${reward.reward_description} vests on ${formatDate(reward.vest_dt)}`,
          ),
        ],
        alert:
          (!!getOfferChangeDetails.data?.earned_rewards?.length ||
            !!getOfferChangeDetails.data?.forfeitable_rewards?.length) &&
          "Pending Rewards Alert! This person will lose rewards by changing plans.",
      },
    ],
    newOffer: [
      {
        title: "New Offer",
        value: offer.offer_code,
      },
      {
        title: "New Plan",
        value: offer.plan_code,
      },
      {
        title: "New Rate",
        value: offer.rate,
      },
      {
        title: "New Plan Rewards",
        value: [
          ...(Array.isArray(offer.plan_promos) ? offer.plan_promos : []).map(
            (promo) => promo.promo_desc,
          ),
          ...(Array.isArray(offer.plan_loyalty_programs)
            ? offer.plan_loyalty_programs
            : []
          ).map((loyalty) => loyalty.loyalty_program_desc),
        ],
      },
    ],
  }

  return {
    data,
    isError: getAccountQuery.isError,
    isPending: getAccountQuery.isPending || getOfferChangeDetails.isPending,
    error: [
      getAccountQuery.error?.message,
      getOfferChangeDetails.error?.message,
    ]
      .filter(Boolean)
      .join(" | "),
  }
}
